import { Link } from "react-router-dom";
import { useState, useEffect, useCallback, useMemo } from "react";
import { ArrowRight } from "lucide-react";
import { api } from "@/utils/api-client";
import { LoadingState } from "@/components/LoadingState";
import { PageHero } from "../_components/page-hero";

type BlogPost = {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  coverImage: string;
  author: string;
  tags: string[];
  publishedAt: string;
};

function fmt(date: string) {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-IN", { month: "short", day: "numeric", year: "numeric" });
}

export default function BlogListPage() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPosts = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<Record<string, unknown>[]>("/api/public/blog")
      .then((data) => setPosts((data || []).map((p) => ({ ...p, id: (p.id as string) || String(p._id) } as BlogPost))))
      .catch(() => setError("Failed to load blog posts"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const [featured, ...rest] = useMemo(() => posts, [posts]);

  return (
    <>
      <PageHero
        eyebrow="Dispatches"
        title={<>The rental <span className="gradient-text">field notes.</span></>}
        sub="Practical guides to the booking loop, customer records, deposits and fleet documents."
      />

      <section className="pb-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <LoadingState loading={loading} error={error} onRetry={fetchPosts} loadingMessage="Loading…">
            {posts.length === 0 ? (
              <p className="py-16 text-center text-sm text-muted-foreground">No posts yet. Check back soon.</p>
            ) : (
              <>
                {featured && (
                  <Link
                    to={`/blog/${featured.slug}`}
                    className="group block overflow-hidden rounded-3xl border border-border bg-surface/40 transition-all hover:bg-surface/70"
                  >
                    <div className="grid md:grid-cols-2">
                      {featured.coverImage ? (
                        <div
                          className="aspect-[16/10] md:aspect-auto"
                          style={{ backgroundImage: `url(${featured.coverImage})`, backgroundSize: "cover", backgroundPosition: "center" }}
                        />
                      ) : (
                        <div className="aspect-[16/10] bg-gradient-mesh md:aspect-auto" />
                      )}
                      <div className="flex flex-col justify-center p-8 sm:p-10">
                        <div className="text-xs uppercase tracking-wider text-primary">
                          {featured.tags?.[0] || "Guide"} · Featured
                        </div>
                        <h2 className="mt-3 font-display text-2xl font-semibold leading-tight tracking-tight transition-colors group-hover:text-primary sm:text-3xl">
                          {featured.title}
                        </h2>
                        {featured.excerpt && <p className="mt-3 text-muted-foreground">{featured.excerpt}</p>}
                        <div className="mt-6 flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">{fmt(featured.publishedAt)}</span>
                          <span className="inline-flex items-center gap-1.5 text-primary">
                            Read <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                )}

                {rest.length > 0 && (
                  <div className="mt-16 grid gap-x-10 gap-y-12 md:grid-cols-2 lg:grid-cols-3">
                    {rest.map((p) => (
                      <Link key={p.id} to={`/blog/${p.slug}`} className="group block">
                        {p.coverImage ? (
                          <div
                            className="aspect-[16/9] overflow-hidden rounded-xl border border-border"
                            style={{ backgroundImage: `url(${p.coverImage})`, backgroundSize: "cover", backgroundPosition: "center" }}
                          />
                        ) : (
                          <div className="aspect-[16/9] rounded-xl border border-border bg-gradient-mesh" />
                        )}
                        <div className="mt-5">
                          {p.tags?.[0] && <div className="text-xs uppercase tracking-wider text-primary">{p.tags[0]}</div>}
                          <h3 className="mt-2 font-display text-xl font-semibold leading-snug transition-colors group-hover:text-primary">
                            {p.title}
                          </h3>
                          {p.excerpt && <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{p.excerpt}</p>}
                          <div className="mt-4 text-xs text-muted-foreground">{fmt(p.publishedAt)}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </>
            )}
          </LoadingState>
        </div>
      </section>
    </>
  );
}
